import { useEffect, useState } from 'react';
// ISS-5690 - MATCH FOLLOW
import {
  ICON_MATCH_FOLLOW,
  ICON_MATCH_UNFOLLOW,
  IMatchFollow,
  MatchFollowIcon,
} from '@sky-it-livedata/livedata-sports-design-system';

import {
  LABEL_MATCH_FOLLOW,
  LABEL_MATCH_UNFOLLOW,
  localStorageExpiresAtKey,
  localStorageMatchFollowKey,
  localStorageMatchIdKey,
  localStorageModalIdKey,
  storageGetValue,
  storageSetValue,
} from './storage';

// Use STATE and EXPORT as CUSTOM HOOK!
export const useMatchFollow = (currMatchId?: string | number, matchStartDate?: Date): IMatchFollow | undefined => {
  const [matchId, setMatchId] = useState<string | number>();
  const [label, setLabel] = useState(LABEL_MATCH_FOLLOW);
  const [className, setClassName] = useState<MatchFollowIcon>(ICON_MATCH_FOLLOW);
  const [matchFollow, setMatchFollow] = useState(false);

  // There's already a MATCH ID in LOCAL STORAGE?
  const prevMatchId = storageGetValue(localStorageMatchFollowKey, localStorageMatchIdKey);

  useEffect(() => {
    setMatchFollow(currMatchId?.toString() === prevMatchId);
  }, [currMatchId, prevMatchId, matchId]);

  useEffect(() => {
    setLabel(matchFollow ? LABEL_MATCH_UNFOLLOW : LABEL_MATCH_FOLLOW);
    setClassName(matchFollow ? ICON_MATCH_UNFOLLOW : ICON_MATCH_FOLLOW);
  }, [matchFollow]);

  // LISTENER for LOCAL STORAGE -> to FIRE EVENT when MATCH-FOLLOW is dragged into TRASHBIN (in data-cards)
  useEffect(() => {
    const listener = (event: StorageEvent) => {
      const { key, newValue, oldValue } = event;
      // console.log('useEffect - storage', { event, key, newValue, oldValue, matchId, currMatchId, prevMatchId });

      // ADD/REMOVE ITEM
      if (key === localStorageMatchIdKey) {
        setMatchFollow(Boolean(newValue)); // FIXME: see line #21
        console.info(newValue ? `Match ${newValue} SUBSCRIBED` : `Match ${oldValue} UNSUBSCRIBED`); // eslint-disable-line no-console
      }
    };

    window.addEventListener('storage', listener);
    return () => window.removeEventListener('storage', listener);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkMatchId = (id: string | number) => {
    // console.log('checkMatchId', { id, matchId, currMatchId, prevMatchId });
    if (prevMatchId && String(id) !== String(prevMatchId)) {
      // console.log('checkMatchId - NEW MATCH FOLLOW?', { id, prevMatchId });
      storageSetValue(localStorageMatchFollowKey, localStorageModalIdKey, id.toString());
      // DATA-CARD storage observer will show a MODAL
      dispatchEvent(
        new StorageEvent('storage', {
          key: localStorageModalIdKey,
          newValue: id.toString(),
          oldValue: prevMatchId.toString(),
        })
      ); // See https://stackoverflow.com/a/65348883/2278141
    } else if (matchFollow) {
      // console.log('checkMatchId - MATCH UNFOLLOW', { matchFollow });
      // UNSUBSCRIBE current match
      localStorage.removeItem(localStorageMatchFollowKey);

      dispatchEvent(
        new StorageEvent('storage', {
          key: localStorageMatchIdKey,
          newValue: null,
          oldValue: `${prevMatchId}`,
          url: 'unfollow', // See MatchFollowAction type in livedata-data-cards-web-components-app/src/components/match-follow/match-follow.tsx
        })
      ); // See https://stackoverflow.com/a/65348883/2278141
      setMatchId('cta_segui_partita_click'); // Must be DIFFERENT from DEFAULT VALUE to FIRE STATE UPDATE!
    } else {
      // console.log('checkMatchId - MATCH-FOLLOW', { id, prevMatchId });
      const expiresAt = (matchStartDate?.getTime() ?? 0) + 8 * 60 * 60 * 1000;
      storageSetValue(localStorageMatchFollowKey, localStorageMatchIdKey, id.toString());
      storageSetValue(localStorageMatchFollowKey, localStorageExpiresAtKey, expiresAt.toString()); // FIXME: actually HARD to TEST due to DATA in SVIL/COLL

      dispatchEvent(
        new StorageEvent('storage', {
          key: localStorageMatchIdKey,
          newValue: id.toString(),
          oldValue: `${prevMatchId}`,
          url: 'follow', // See MatchFollowAction type in livedata-data-cards-web-components-app/src/components/match-follow/match-follow.tsx
        })
      ); // See https://stackoverflow.com/a/65348883/2278141
      setMatchId(id);
    }
  };

  return currMatchId
    ? {
        label,
        className,
        callback: (clickedId: string | number) => {
          checkMatchId(clickedId);
        },
      }
    : undefined;
};
